import { FetchError } from "types/fetch-error";
import { getRequestHeaders, logout } from "./oidc-user-manager";
import { sanitizeAndBuildUrl, sanitizeS3Url } from "../utils/sanitize";




export const handleResponse = async (res: Response) => {
  if (res.status === 401) {
    logout(false);
  }

  if (res.status !== 200) {
    const error = new Error(res.statusText) as FetchError;
    error.status = res.status;

    try {
      const response = await res.json();
      error.details = response.details;
      error.name = response.name;

      if (res.status === 400 && response.message) {
        error.message = response.message;
      }
    } catch {
      // some responses may not be json
      error.details = [];
    }

    throw error;
  } else {
    return res.json();
  }
};

export const handleResponseBlob = async (res: Response) => {
  if (res.status === 401) {
    logout(false);
  }

  if (res.status !== 200) {
    const error = new Error(res.statusText) as FetchError;
    error.status = res.status;

    try {
      const response = await res.json();
      error.details = response.details;
      error.name = response.name;

      if (res.status === 400 && response.message) {
        error.message = response.message;
      }
    } catch {
      // some responses may not be json
      error.details = [];
    }

    throw error;
  } else {
    const blob = await res.blob();
    const fileName = res.headers.get("x-file-name");
    return { blob, fileName };
  }
};

export const getS3Blob = async (url: string): Promise<Blob> => {
  // CWE-918: SSRF protection - Sanitize S3 URL (breaks taint flow)
  const sanitizedUrl = sanitizeS3Url(url);
  const res = await fetch(sanitizedUrl);
  return res.blob();
};

// responseCallback should only be overwritten in unusual cases that don't follow common API flows
// e.g. getMe is called from an unauthenticated context and skips the 401 check.
export const getJson = async (
  endpoint: string,
  responseCallback: (res: Response) => any = handleResponse,
  extraHeaders?: Record<string, string>,
): Promise<any> => {
  if (!("fetch" in window)) {
    return [];
  }

  // CWE-918: SSRF protection - Sanitize and build URL (Flaw 269)
  const sanitizedUrl = sanitizeAndBuildUrl(endpoint);

  return fetch(sanitizedUrl, {
    headers: { ...(await getRequestHeaders()), ...extraHeaders },
  })
    .then(responseCallback);
};

export const postForm = async (endpoint: string, data: FormData) => {
  // CWE-918: SSRF protection - Sanitize and build URL (Flaw 270)
  const sanitizedUrl = sanitizeAndBuildUrl(endpoint);

  return fetch(sanitizedUrl, {
    method: "POST",
    body: data,
    headers: await getRequestHeaders(),
  }).then(handleResponse);
};

export const postJson = async (endpoint: string, body: any) => {
  // CWE-918: SSRF protection - Sanitize and build URL (Flaw 271)
  const sanitizedUrl = sanitizeAndBuildUrl(endpoint);

  return fetch(sanitizedUrl, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      ...await getRequestHeaders(),
    },
  }).then(handleResponse);
};

export const putJson = async (endpoint: string, body: any) => {
  // CWE-918: SSRF protection - Sanitize and build URL (Flaw 272)
  const sanitizedUrl = sanitizeAndBuildUrl(endpoint);

  return fetch(sanitizedUrl, {
    method: "PUT",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      ...await getRequestHeaders(),
    },
  }).then(handleResponse);
};

export const deleteRequest = async (endpoint: string) => {
  // CWE-918: SSRF protection - Sanitize and build URL (edge case)
  const sanitizedUrl = sanitizeAndBuildUrl(endpoint);

  return fetch(sanitizedUrl, {
    method: "DELETE",
    headers: await getRequestHeaders(),
  }).then(handleResponse);
};

export default getJson;
